/**
 * Keeps an accordion heading steady when it is opened or closed.
 *
 * When another open panel above collapses (single-open accordions), the page
 * content shifts and the tapped heading jumps away. We remember where the
 * heading was, let React/Radix apply the change, then undo the shift and — only
 * if needed — nudge the page so the heading and the start of its content sit
 * clear of the fixed header and fixed bottom navigation.
 */
function fixedHeaderHeight(): number {
  const header = document.querySelector("header");
  if (!header) return 0;
  const cs = getComputedStyle(header);
  if (cs.position !== "fixed" && cs.position !== "sticky") return 0;
  const r = header.getBoundingClientRect();
  return r.bottom > 0 ? r.bottom : 0;
}

function fixedBottomHeight(): number {
  const nav = document.querySelector<HTMLElement>("[data-bottom-nav], nav[aria-label*='bottom' i]");
  if (!nav) return 0;
  const r = nav.getBoundingClientRect();
  return r.top < window.innerHeight ? window.innerHeight - r.top : 0;
}

export function anchorToggle(el: HTMLElement | null | undefined, apply: () => void) {
  if (!el || typeof window === "undefined") {
    apply();
    return;
  }
  const before = el.getBoundingClientRect().top;
  apply();
  const start = performance.now();
  // Collapse animations run ~200ms, so keep correcting drift until they finish.
  const settle = (_pass: number) => {
    const after = el.getBoundingClientRect().top;
    const drift = after - before;
    if (Math.abs(drift) > 1) window.scrollBy({ top: drift, behavior: "instant" as ScrollBehavior });
    if (performance.now() - start < 320) {
      requestAnimationFrame(() => settle(_pass + 1));
      return;
    }
    // Final pass: make sure heading + beginning of content are visible.
    const top = el.getBoundingClientRect().top;
    const minTop = fixedHeaderHeight() + 8;
    const maxTop = window.innerHeight - fixedBottomHeight() - 160; // leave room for content start
    if (top < minTop) window.scrollBy({ top: top - minTop, behavior: "smooth" });
    else if (top > maxTop && maxTop > minTop) window.scrollBy({ top: top - maxTop, behavior: "smooth" });
  };
  requestAnimationFrame(() => settle(0));
}
